import { makeAutoObservable, runInAction } from 'mobx'
import { getDescendants } from '@minoru/react-dnd-treeview'
import { RequestCancelAxios } from 'shared/api/lib'
import { applyCustomSort, logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IInboxTreeCombine, type INodeModel, TreeRootInboxes } from 'entities/Inbox'
import { UsersApi } from 'entities/Users'
import type { ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { searchFilter } from 'pages/conversations/ui/ConversationMenu/lib/searchFilter'

const getFavoriteNodeId = (node: INodeModel<IInboxTreeCombine>) => {
  if (!node.data) return String(node.id)

  return `${node.data.type}:${node.data.id}`
}

export class FavoritesMenuStore {
  private _search = ''
  private _favoritesOrder: string[] = []
  private _cancelRequestLoadFavoritesOrder = new RequestCancelAxios()

  constructor(
    private _conversationMenuStore: ConversationMenuStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  handleUpdateSearch = (value: string) => {
    this._search = value
  }

  private get _selectInboxesFavoriteFreeFilter() {
    if (!this._search) return this._conversationMenuStore.selectInboxesFreeSort

    return this._conversationMenuStore.selectInboxesFreeSort.filter((inboxTree) => {
      return searchFilter(inboxTree.data, this._search, this._entities)
    })
  }

  private get _allFavoriteTopLevelNodes() {
    return getDescendants(
      this._conversationMenuStore.selectInboxesFreeSort,
      TreeRootInboxes.FAVORITE
    ).filter((node) => node.parent === TreeRootInboxes.FAVORITE)
  }

  get items() {
    const descendants = getDescendants(
      this._selectInboxesFavoriteFreeFilter,
      TreeRootInboxes.FAVORITE
    )

    if (!this._favoritesOrder.length) return descendants

    const topLevel = descendants.filter((node) => node.parent === TreeRootInboxes.FAVORITE)
    const nested = descendants.filter((node) => node.parent !== TreeRootInboxes.FAVORITE)

    const sortedTopLevel = applyCustomSort(topLevel, this._favoritesOrder, getFavoriteNodeId, {
      newItemsAt: 'bottom',
    })

    return [...sortedTopLevel, ...nested]
  }

  get search() {
    return this._search
  }

  get hideInboxes() {
    return this.items.length === 0 && this.search === ''
  }

  private get _selectInboxesFilter() {
    return this._entities.inboxesStore.personalInboxes
  }

  private get _selectInboxes() {
    return this._selectInboxesFilter.filter((item) => item.isFavoriteType)
  }

  get selectInboxesCount() {
    return this._selectInboxes.map((item) => item.unread).reduce((state, value) => state + value, 0)
  }

  loadFavoritesOrder = async () => {
    try {
      this._cancelRequestLoadFavoritesOrder.init()

      const { data } = await UsersApi.getUsersUiSettings('sort-inbox-favorite', {
        cancelToken: this._cancelRequestLoadFavoritesOrder.token,
      })

      runInAction(() => {
        this._favoritesOrder = data.data?.items ?? []
      })
    } catch (e) {
      logger.error(e)
    }
  }

  reorderFavorites = async (orderedTopLevelNodes: INodeModel<IInboxTreeCombine>[]) => {
    try {
      this._cancelRequestLoadFavoritesOrder.reset()

      const newVisibleIds = orderedTopLevelNodes.map(getFavoriteNodeId)
      const newVisibleSet = new Set(newVisibleIds)

      const fullCurrentOrder = applyCustomSort(
        this._allFavoriteTopLevelNodes,
        this._favoritesOrder,
        getFavoriteNodeId,
        { newItemsAt: 'bottom' }
      ).map(getFavoriteNodeId)

      let visibleIdx = 0
      const merged = fullCurrentOrder.map((id) => {
        if (!newVisibleSet.has(id)) return id

        const replacement = newVisibleIds[visibleIdx] ?? id
        visibleIdx++

        return replacement
      })

      const isSameOrder =
        merged.length === this._favoritesOrder.length &&
        merged.every((id, index) => id === this._favoritesOrder[index])

      if (isSameOrder) return

      runInAction(() => {
        this._favoritesOrder = merged
      })

      await UsersApi.updateUsersUiSettings({
        source_type: 'sort-inbox-favorite',
        items: merged,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  dispose = () => {
    this._cancelRequestLoadFavoritesOrder.reset()
  }
}
